import {CONSUMER_UNKNOWN, resolveConsumer, tryDecodeJwtPayload} from "./consumer";

/**
 * Standard telemetry attributes (New Relic) that split Standalone and Headless journeys
 * by consumer and environment. Never put tokens, paths or personal data here.
 */

export const RUNTIME_STANDALONE = 'standalone';
export const RUNTIME_HEADLESS = 'headless';

export const ENTRYPOINT_STANDALONE = 'standalone';
export const ENTRYPOINT_EMBEDDABLE = 'embeddable';

export const TENANT_FRANCHISE = 'cupfranchise';
export const TENANT_HEADLESS = 'cupheadless';

export const CONSUMER_STANDALONE = 'standalone';

export const ENVIRONMENT_UNKNOWN = 'unknown';

// Keep in sync with the New Relic account guard in public/index.html
export const ENVIRONMENTS = ['dev', 'stg', 'prod'];

// Routes that carry the JWT in the path (/embeddable/home/:jwt and /embeddable/lesson/:jwt)
const EMBEDDABLE_JWT_ROUTE = /\/embeddable\/(?:home|lesson)\/([^/?#]+)/;

export function normalizeEnvironment(value) {
    const environment = typeof value === 'string' ? value.trim() : '';
    return ENVIRONMENTS.indexOf(environment) !== -1 ? environment : ENVIRONMENT_UNKNOWN;
}

export function isEmbeddablePath(pathname) {
    return typeof pathname === 'string' && pathname.indexOf('/embeddable') !== -1;
}

export function isEmbeddableJwtPath(pathname) {
    return typeof pathname === 'string' && EMBEDDABLE_JWT_ROUTE.test(pathname);
}

function safeDecodeURIComponent(value) {
    try {
        return decodeURIComponent(value);
    } catch (_e) {
        return value;
    }
}

/**
 * Resolves the consumer from the (unverified) claims of the JWT in the route, so the first PageView/AJAX
 * are already classified before /wizportal/jwt_validate answers. The login flow re-reports it afterwards.
 */
export function consumerFromJwtPath(pathname) {
    const match = typeof pathname === 'string' ? EMBEDDABLE_JWT_ROUTE.exec(pathname) : null;
    return resolveConsumer(match ? tryDecodeJwtPayload(safeDecodeURIComponent(match[1])) : null);
}

/**
 * Builds the telemetry attributes for the current route/context.
 * On JWT routes the consumer comes from the token claims; elsewhere from the user stored at login.
 */
export function buildTelemetryContext({isEmbedMode, pathname, user, deployEnv}) {
    const headless = Boolean(isEmbedMode);
    const context = {
        'catchup.runtime_mode': headless ? RUNTIME_HEADLESS : RUNTIME_STANDALONE,
        'catchup.entrypoint': isEmbeddablePath(pathname) ? ENTRYPOINT_EMBEDDABLE : ENTRYPOINT_STANDALONE,
        'catchup.tenant': headless ? TENANT_HEADLESS : TENANT_FRANCHISE,
        'deployment.environment': normalizeEnvironment(deployEnv),
    };

    if (!headless) {
        // Standalone traffic is never classified as a Headless consumer (e.g. portal)
        context['catchup.consumer'] = CONSUMER_STANDALONE;
        context['catchup.consumer_id'] = CONSUMER_STANDALONE;
    } else if (isEmbeddableJwtPath(pathname)) {
        const info = consumerFromJwtPath(pathname);
        context['catchup.consumer'] = info.consumer;
        context['catchup.consumer_id'] = info.consumerId;
        if (info.consumerRaw) {
            context['catchup.consumer_raw'] = info.consumerRaw;
        }
    } else {
        const data = user || {};
        context['catchup.consumer'] = data.consumer || CONSUMER_UNKNOWN;
        context['catchup.consumer_id'] = data.consumer_id || CONSUMER_UNKNOWN;
    }

    return context;
}
